<template>

  <div>

    <approval-remarks :row-data="actionData" :steps="actionData.steps" :legal-step="3" />

    <section v-if="isBackhaul">

        <!-- XOL-552: "+ Backhaul" sits above the site's line table so a Tower Co
             user can add the missing line without leaving the RTB. It opens the
             full BackhaulProvider form in new mode, stamped with this RTB, with
             Provider = the telco's Backhaul twin and Type = the RTB's type.
             `sharer-id` is deliberately NOT passed: it would flip the popup to
             "Customer". `:table-ref="null"` stops the stray
             backhaultable-refresh-table broadcast. The new line is not
             auto-selected — the list refreshes and the user picks. -->
        <template v-if="isTowerCo() && actionData.subActionType === 'CUST'">
          <!-- XOL-596 (completed steps, CS-B): "+ Backhaul" creates a line, so it is not
               rendered while step 3 is complete (a disabled button's tooltip can't be reached). -->
          <div v-if="!frozen" class="text-right">
            <backhaul-action
              :provider="true"
              button-text="Backhaul"
              :site-id="actionData.site_id"
              :site-name="site ? site.siteName : null"
              :action-id="actionData.id"
              :default-provider-id="telcoBackhaulTwinId"
              :default-backhaul-type="actionData.backhaulType"
              :action-step-id="step3ActionStepId"
              :read-only="readOnly"
              :table-ref="null"
              close-on-save
              @saved="onBackhaulLineAdded" />
          </div>

          <backhaul-customer
            ref="bhCustomer"
            :sharer-id="actionData.organisation_id"
            :site-id="actionData.site_id"
            :action-id="actionData.id"
            :site-code="site ? site.siteId : null"
            :site-name="site ? site.siteName : null"
            :read-only="readOnly"
            :step-frozen="frozen"
            :action-step-id="step3ActionStepId"
            :is-request="true" />
        </template>

        <!-- XOL-526 (C4, UX §2.2): a Telco is no longer mounted here at all —
             the backhaul details are the Tower Co's and the provider's, not
             the telco's. `:editable` and the mount condition are the SAME
             expression: a Tower Co or the provider can land here; nobody
             else does. lockedForViewer inside BackhaulProvider still holds
             the actual editing controls to Tower-Co-only. -->
        <backhaul-provider
          v-else-if="isTowerCo() || isRequestProvider()"
          :provider-id="actionData.provider_id"
          :site-id="actionData.site_id"
          :action-id="actionData.id"
          :editable="isTowerCo() || isRequestProvider()"
          :step-frozen="frozen"
          :action-step-id="step3ActionStepId"
          :is-request="true" />

    </section>

    <section v-if="isTowerCo() && canAccess('finance')">

        <el-form :inline="true">
            <el-form-item label="Agreement Type">

              <el-select
                v-model="step3.docType"
                placeholder="Select"
                filterable
                allow-create
                :disabled="!canAccess('finance') || disabled || frozen || locked"
                :aria-describedby="(locked && !disabled) ? 'step3-approval-lock-line' : null"
                @change="docTypeChanged">

                <el-option
                   v-for="item in types"
                   :key="item.value"
                   :label="item.label"
                   :value="item.value">
                 </el-option>

              </el-select>
              <field-cue :row="actionData" path="state.step3.docType" label="Agreement Type" layout="inline" />

          </el-form-item>
        </el-form>


      <p>Agreement Type: <b>{{step3.docType}}</b></p>

      <el-collapse v-model="accordion" v-if="!disabled" accordion>

      <!-- XOL-596 (Carlos, 2026-10-14: "Move the lock to the top of the section, aligned to
           the right, just left of the toggle section arrow"): the lock line sits on this
           collapse's HEADER row, as a sibling of the item (see step2.vue). -->
      <div v-if="isTowerCo()" class="approval-collapse" :class="{ 'approval-collapse--line': approvalLineShown(step) }">
      <!-- XOL-596 (approval lock): locked by whom, or "Not locked" with Lock. -->
      <approval-lock-line
        ref="lockLine"
        placement="header"
        :row="step"
        :title="step.stepName"
        :decided="approvalDecided(step)"
        :frozen="frozen"
        :can-lock="canLockApproval(step) && !readOnly && !frozen"
        :can-unlock="canUnlockApproval(step) && !readOnly && !frozen"
        :busy="lockRunning !== null"
        :busy-label="lockBusyLabel()"
        :format="formatDateTime"
        text-id="step3-approval-lock-line"
        @lock="lockApproval(step)"
        @unlock="unlockApproval(step)" />
      <el-collapse-item title="Finance Approval of Legal Document" name="FinApprove">

          <el-alert type="info" :title="'Approval of ' + (step3.docType ? step3.docType : 'legal documents')" show-icon :closable="false">
            <!-- XOL-596 (decision fields): the tick is a FIELD. It saves at the form's pause
                 like every other; the C-12 card carries Pending, Saving and Saved. -->
            <check-box v-model="step3.legalApproved" no-form :disabled="locked || frozen" @change="legalChecked">{{(step3.docType ? step3.docType : 'Legal Document')}}
              <template #cue><field-cue :row="actionData" path="state.step3.legalApproved" label="Finance Approval of Legal Document" layout="inline" dirty /></template>
            </check-box>
            <span v-if="step.approved"><b>{{formatDate(step.approved_at)}}</b> by <b>{{step.approved_by}}</b></span>
          </el-alert>

          <!-- XOL-596 (readable remarks): the heading was a closable `type="info1"` (an invalid
               type, so unstyled) el-alert shown only while the request was active. It is now a
               real heading, the same element above the editor and the read-only block. -->
          <section class="remarks-section">
            <h5 class="remarks-heading">Progress Remarks</h5>

            <!-- XOL-596 (completed steps, CS-R; approval lock, 2026-10-15): a read-only block with an
                 explicit empty state, for a completed (CS-R) or a locked approval. -->
            <!-- XOL-608: the editor renders only when it would be enabled. A viewer who cannot
                 write it (a request that went inactive while the drawer was open) gets the block,
                 never a disabled HtmlInput. -->
            <read-only-remarks v-if="financeRemarksReadOnly" aria-label="Progress Remarks" :html="step.remarks" />
            <template v-else>
              <html-input v-model="step.remarks" :disabled="isViewOnly() || !this.isActive(this.actionData)"
                @focus="holdField(step, 'remarks')" @blur="releaseField(step, 'remarks')" @input="progressRemarksChanged" />
              <field-cue :row="step" path="remarks" label="Progress Remarks" layout="block" />
            </template>
          </section>

      </el-collapse-item>
      </div>

      <el-collapse-item v-if="isTowerCo() && !disabled && step.actionType === 'RTS'" title="Create Sharer" name="Sharer">
        <!-- XOL-596 (completed steps, CS-S): a sharer can't be created from a completed step.
             The collapse item stays, so the user can still find it, and says why. A sharer that
             was already created is shown as it was (its form is disabled through stepSaved). -->
        <p v-if="frozen && !step3.saved" class="text-muted"><i class="fa fa-lock" aria-hidden="true"></i> {{sharerNote}}</p>

        <template v-else>
        <el-row>
          <h2 v-if="!step3.saved"><strong class="text-success">Add</strong> New Sharer</h2>
          <h3 v-else>Sharer already created</h3>
        </el-row>

        <licensee-form
        :frozen="frozen"
        :action-step-id="step3ActionStepId"
        :site-id="rowData.site_id"
        :tower-id="site.siteId"
        :sharer-name="actionData.organisation.name"
        :sharer-id="actionData.organisation.id"
        :editable="step3.saved"
        :saved="step3.saved"
        :sharer-site-id="step2p2.state.siteId"
        :service-type="step2p2.state.serviceType"
        :spectrum-band="step2p2.state.spectrumBand"
        :equipment="step2.equipment" />
        <!-- @TODO need to figure out how to handle this: :editable="true" -->
        </template>
      </el-collapse-item>

    </el-collapse>

      <!-- XOL-608 (Q1 default): a finance-access Tower Co viewer who cannot write the step (`disabled`:
           `!canWriteStep() || isViewOnly() || readOnly`) has no Finance
           collapse (`v-if="!disabled"` above), so these remarks vanished. The same block every other
           Tower Co viewer already gets below, where the collapse would render. The collapse and its
           tick stay hidden. -->
      <read-only-remarks v-else label="Progress Remarks" :html="step.remarks" />

    </section>


    <section v-else>

      <div v-if="isTowerCo()">
          <!-- XOL-596 (readable remarks): always shown, with "No remarks" when empty (the
               field used to vanish with no label), framed like every other read-only remarks. -->
          <read-only-remarks label="Progress Remarks" :html="step.remarks" />
        </div>

        <!-- Carlos, 2026-10-15: a counterparty's Progress Remarks is its party-2 row's field, which is
             written only on an RTB; on another request type it is not part of the form. -->
        <div v-else>
          <section v-if="stepRemarksInForm(step)" class="remarks-section">
            <h5 class="remarks-heading">Progress Remarks</h5>

            <!-- XOL-526 (D5, S5): was an undisabled html-input — ANY
                 non-Tower-Co viewer (telco or provider) saw a live editor
                 with no gate at all, though a save had nowhere to go (PUT
                 /step was role:towerco pre-API-5). canWriteOwnStep() now
                 offers a real write, folding in the party-1-step-completed
                 lock (D9/A10). -->
            <!-- XOL-596 (completed steps, CS-R): a read-only block for every viewer. -->
            <!-- XOL-608: also for a viewer who cannot write it (view-only, a party that is not the
                 owner or provider, a read-only drawer): the editor renders only when it would be
                 enabled. -->
            <read-only-remarks v-if="progressRemarksReadOnly" aria-label="Progress Remarks" :html="step.remarks"
              :stamp="progressRemarksStamp ? lastUpdatedLine(progressRemarksStamp) : null" />
            <template v-else>
              <small v-if="progressRemarksNote" class="text-muted">{{progressRemarksNote}}</small>
              <html-input v-model="step.remarks" :disabled="!canWriteOwnStep(step) || readOnly" no-form
                @focus="holdField(step, 'remarks')" @blur="releaseField(step, 'remarks')" @input="fieldChanged(step, 'remarks')" />
              <field-cue :row="step" path="remarks" label="Progress Remarks" layout="block" />
              <small v-if="progressRemarksStamp" class="remarks-stamp">{{lastUpdatedLine(progressRemarksStamp)}}</small>
            </template>

          </section>
        </div>

    </section>

  </div>

</template>

<script>

import RequestMixins from '@/mixins/RequestMixins'

import FileDownload from '@/components/atoms/files/FileDownload'
import FileUpload from '@/components/atoms/files/FileUpload'

import LicenseeForm from '@/components/requests/LicenseeForm'

import BackhaulAction from '@/components/actions/BackhaulAction'
import BackhaulCustomer from '@/components/backhaul/BackhaulCustomer'
import BackhaulProvider from '@/components/backhaul/BackhaulProvider'

import { findBackhaulTwinId } from '@/lib/backhaulTwin'

import ApprovalRemarks from '@/components/requests/workflow/ApprovalRemarks'
import FieldCue from '@/components/requests/FieldCue'
import ApprovalLockLine from '@/components/requests/ApprovalLockLine'
import ReadOnlyRemarks from '@/components/atoms/ReadOnlyRemarks'

import { copyText } from '@/lib/requestSyncCopy'

export default {

  // XOL-534 (reviewer): DateMixins already arrives transitively via
  // RequestMixins -> SiteMixins — a direct import here was redundant.
  mixins: [RequestMixins],

  components: {
    ApprovalLockLine,
    ApprovalRemarks,
    BackhaulAction,
    BackhaulCustomer,
    BackhaulProvider,
    FieldCue,
    FileDownload,
    FileUpload,
    LicenseeForm,
    ReadOnlyRemarks
  },

  props: {

    rowData: {
      type: Object,
      required: true
    },

    rowIndex: {
      type: Number,
      default: null
    },

    stepData: {
      type: Object,
      default: null
    },

    readOnly: {
      type: Boolean,
      default: true
    },

    isClosed: {
      type: Boolean,
      default: false
    }

  },



  methods: {

    // XOL-552: "+ Backhaul" saved a line — re-read the list. Deliberately does
    // NOT select the new row (the user picks it).
    onBackhaulLineAdded () {
      if (this.$refs.bhCustomer) {
        this.$refs.bhCustomer.refreshLines()
      }
    },

    // XOL-596: the fresh GET on open is adopted IN PLACE into the one object for
    // the row (F4), with the seq of when it was issued.
    loadOwnStep () {
      this.loadStep(this.stepData.id, this.step, this.afterStepLoad)
    },

    afterStepLoad (step) {
      // To handle existing RTx without this flag. (`legalApproved` is not a
      // column; it is a display flag on the row object.)
      step.legalApproved = (this.actionData.status !== 'Closed' ? step.legalApproved : true)
    },

    // XOL-596 (decision fields, Carlos 2026-10-14): the legal-approval tick is a FIELD.
    // It marks the field changed and restarts the form's pause; at the pause ONE
    // approval command goes out, `POST /step/{id}/approval` with `expectedApproved` (a
    // boolean, contract C2) = the tick the user SAW (the field's base, never the
    // check-box, which has already flipped). Nothing is stamped locally (approved,
    // approved_at, approved_by): the server decides, mirrors it into
    // `state.step3.legalApproved`, and the adopt of the response shows it. Ticked and
    // unticked within the pause sends nothing.
    legalChecked () {
      // The section is Tower Co + finance only and the control `!disabled`; this is the
      // handler's own guard. A LOCKED approval is read-only.
      if (this.disabled || this.locked || this.frozen) {
        var rec = this.syncEnsureDecision(this.actionData)

        if (rec) {
          this.putBackDecision(rec, rec.decision.paths)
        }

        return false
      }

      return this.decisionChanged(this.actionData, 'state.step3.legalApproved')
    },

    // Agreement Type: a LOCKED or completed step 3 is read-only (2026-10-15). The select is
    // disabled, so this is the handler's own guard (defence in depth, like legalChecked):
    // the displayed value is put back and nothing is recorded. A lock that arrives AFTER a
    // change was recorded is the server's to refuse (403 approval_locked), at the save.
    docTypeChanged () {
      if (this.disabled || this.locked || this.frozen || !this.canAccess('finance')) {
        this.restoreServerValue(this.actionData, 'state.step3.docType')
        return false
      }

      return this.fieldChanged(this.actionData, 'state.step3.docType')
    },

    // Tower Co Progress Remarks (party-1 row): read-only while locked or completed. The
    // editor is unmounted then, so this is the same defence in depth.
    progressRemarksChanged () {
      if (this.locked || this.frozen) {
        this.restoreServerValue(this.step, 'remarks')
        return false
      }

      return this.fieldChanged(this.step, 'remarks')
    }

  },



  watch: {

    // The editors unmount when the step freezes, and an unmounting quill gets no blur: let go
    // of the text path they hold, so nothing stays held by focus.
    frozen (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    },

    // The editor unmounts when the approval locks (an adopt), and an unmounting quill gets no
    // blur: let go of the path it holds. Agreement Type is an `@change` select that is never
    // held, so it has nothing to release.
    locked (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    },

    // XOL-608: the editors also unmount when the viewer can no longer write them (the request
    // goes inactive, a counterparty stops being a writer), and an unmounting quill gets no blur.
    financeRemarksReadOnly (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    },

    progressRemarksReadOnly (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    }

  },


  data () {

    return {
      accordion: 'FinApprove',
      actionData: this.rowData,
      site: this.rowData.site,
      step: this.stepData,
      step2: this.rowData.state.step2,
      step2p2: null,
      step3p1: null,
      step3: this.rowData.state.step3,

      types: [
        {
          value: 'LOO',
          label: 'LOO'
        }, {
          value: 'SLO',
          label: 'SLO'
        }, {
          value: 'AWO',
          label: 'AWO'
        }, {
          value: 'AAWO',
          label: 'AAWO'
        }, {
          value: 'AROW',
          label: 'AROW'
        }
      ]
    }

  }, // data


  computed: {

    // XOL-552: the Backhaul-category org that shares the RTB telco's name
    // (actionData.organisation is appended by the API), or null (e.g. DNB) — the
    // "+ Backhaul" Provider default. NOTE: getConfig() returns a module-level,
    // non-reactive object, so this computed caches the first value it computes
    // (it does not re-evaluate as the config hydrates). That is acceptable: the
    // config loads at boot and is cached in sessionStorage well before an RTB
    // can be opened.
    telcoBackhaulTwinId () {
      return findBackhaulTwinId(this.getConfig().orgGroups, this.actionData.organisation ? this.actionData.organisation.name : null)
    },

    disabled () {
      return !this.canWriteStep() || this.isViewOnly() || this.readOnly
    },

    // XOL-596 (completed steps): step 3 is complete, so everything in it is read-only for
    // every viewer until it is re-opened. A CONSTANT step number, from the server baseline.
    // NOT folded into `disabled`: the Finance collapse is `v-if="!disabled"` and must stay
    // rendered (the lock line lives on its header).
    frozen () {
      return this.isStepFrozen(3)
    },

    // The id of this request's step-3 party-1 row: the step context Create Sharer and the
    // backhaul forms send (`actionStepId`).
    step3ActionStepId () {
      var row = this.partyOneStepRow(3)

      return row ? row.id : null
    },

    sharerNote () {
      return copyText('requestSync.completed.sharer')
    },

    // XOL-596 (approval lock): step 3 locks the legal tick, Agreement Type and the Tower Co
    // Progress Remarks (2026-10-15, reversing Q-L1 / Q-L10); the counterparty's remarks
    // are not covered.
    locked () {
      return this.approvalLocked(this.step)
    },

    // XOL-526 (UX §2.4, U-I): Progress Remarks is the counterparty's own
    // party-2 STEP row — Tower Co's own row (party 1) is a different object
    // and is never shared, so it gets no note/stamp.
    progressRemarksStamp () {
      return (!this.isTowerCo() && this.step) ? this.editStamp(this.step.state, 'remarks') : null
    },

    progressRemarksNote () {
      return (this.canWriteOwnStep(this.step) && !this.isTowerCo() && !this.readOnly && this.hasDistinctProvider())
        ? this.sharedFieldNote('edit these remarks')
        : null
    },

    // XOL-608: the Finance collapse's Progress Remarks (Tower Co with finance access) shows the
    // block exactly when the editor's own `:disabled` would be true (`isViewOnly() ||
    // !isActive(actionData)`), OR'd with the complete / locked terms (XOL-596). A view-only
    // viewer never reaches it (the collapse is `v-if="!disabled"`); the term stays for parity.
    financeRemarksReadOnly () {
      return this.frozen || this.locked || this.isViewOnly() || !this.isActive(this.actionData)
    },

    // XOL-608: the counterparty's Progress Remarks (party-2 row).
    progressRemarksReadOnly () {
      return this.frozen || !this.canWriteOwnStep(this.step) || this.readOnly
    }

  },


  created () {

    if (this.step) {

      // XOL-526 (S5, D9): was `canWriteStep()` — a counterparty may now write its
      // own party-2 row too.
      if ((this.canWriteStep() || this.canWriteOwnStep(this.step)) && !this.readOnly) {
        this.loadOwnStep()
      }

      if (!this.readOnly) {

        if (!this.step.state.hasOwnProperty('saved')) {
          this.step.state.saved = false
        }

        this.step2p2 = this.findWorkflowStep(2, 2, this.rowData.steps)

        if (this.step && this.step.stepNo === 3 && this.step.partyNo === 1) {
          this.step3p1 = this.step
        } else {
          this.step3p1 = this.findWorkflowStep(3, 1, this.rowData.steps)
        }

        this.fixExistingEquipment() // just in case.

      }

    }

  },



  beforeDestroy () {

    // XOL-596: no save here. This used to send the whole step AND the whole
    // action (`saveStep(step, null, actionData, true)`) from whatever copy the
    // drawer held. The fields of this step save at the form's pause or at a flush
    // point; a text field still focused gets no blur on unmount.
    this.releaseFocused()

    if (!this.readOnly && this.step) {
      this.$emit('updated', this.step)
    }
  }

}
</script>
